import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV, CONTACT, IMAGES } from "@/lib/data";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const transparent = location.pathname === "/" && !scrolled;
  const solid = !transparent;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,border-color,padding] duration-500 ${
        solid ? "bg-[color:var(--cream)]/95 backdrop-blur-md border-b border-black/10 py-3 text-[color:var(--ink)]" : "bg-transparent py-5 text-[color:var(--cream)]"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
          <img src={IMAGES.logo} alt="Classic Data Berlin" className="h-11 w-11 object-contain rounded-sm" />
          <div className="hidden sm:block leading-tight">
            <div className={`font-serif text-[16px] tracking-tight ${solid ? "text-[color:var(--ink)]" : "text-[color:var(--cream)]"}`}>Classic Data <span className="italic">Berlin</span></div>
            <div className={`text-[10px] tracking-[0.14em] uppercase leading-tight ${solid ? "text-[color:var(--ink-2)]" : "text-[color:var(--cream)]/85"}`}>
              Bewertungspartner<br />Kfz-Sachverständigenbüro Linkowitz
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.06em] font-medium transition-colors ${
                  isActive
                    ? "text-[color:var(--burgundy)]"
                    : solid
                      ? "text-[color:var(--ink)] hover:text-[color:var(--burgundy)]"
                      : "text-[color:var(--cream)] hover:text-[color:var(--burgundy)]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={`tel:${CONTACT.phoneRaw}`} data-testid="header-phone" className={`hidden md:inline-flex items-center gap-2 text-[13px] font-medium transition-colors ${solid ? "text-[color:var(--ink)] hover:text-[color:var(--burgundy)]" : "text-[color:var(--cream)] hover:text-[color:var(--burgundy)]"}`}>
            <Phone className="h-4 w-4" /> {CONTACT.phone}
          </a>
          <Link to="/kontakt" data-testid="header-cta" className="hidden md:inline-flex btn-primary !py-2.5 !px-4 !text-[11px]">
            Gutachten anfragen
          </Link>
          <button
            data-testid="header-menu-toggle"
            aria-label="Menu"
            className="lg:hidden p-2 border border-black/20 hover:bg-black hover:text-[color:var(--cream)] transition-colors"
            onClick={() => setOpen(v => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[color:var(--cream)] border-t border-black/10"
          >
            <div className="mx-auto max-w-[1400px] px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                  className={({ isActive }) =>
                    `font-serif text-2xl ${isActive ? "text-[color:var(--burgundy)]" : "text-[color:var(--ink)]"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <a href={`tel:${CONTACT.phoneRaw}`} className="mt-3 text-sm text-[color:var(--ink-2)]">Tel. {CONTACT.phone}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
